import { useEffect, useMemo, useState } from "react";
import {
  BookOpenText,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Hash,
  Pencil,
  Plus,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AudienceSelector, { audienceLabel } from "@/components/admin/AudienceSelector";

const empty = {
  title: "",
  summary: "",
  description: "",
  whatsapp_message: "",
  topic: "",
  intention: "",
  position: "standing",
  session_code: "",
  repeat_count: 3,
  starts_at: "",
  duration_minutes: 30,
  audience: "all",
  target_levels: [],
  client_id: "",
  published: true,
};

function localInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function supportedPosition(value) {
  return ["standing", "sitting", "lying"].includes(value) ? value : "standing";
}

function formatSessionDate(value) {
  if (!value) return "Tarih belirlenmedi";
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

const statusText = {
  upcoming: "Yaklaşan",
  live: "Şu an başladı",
  completed: "Tamamlandı",
};

export default function RemoteSessionOffersPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openAttendees, setOpenAttendees] = useState(null);
  const [attendeeData, setAttendeeData] = useState({});

  const load = async () => {
    try {
      const [offers, people] = await Promise.all([
        api.get("/remote-session-offers"),
        api.get("/clients"),
      ]);
      setItems(offers.data || []);
      setClients(people.data || []);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  useEffect(() => { load(); }, []);

  const reset = () => {
    setEditingId(null);
    setForm(empty);
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({
      ...empty,
      ...item,
      client_id: item.client_id || "",
      position: supportedPosition(item.position),
      starts_at: localInputValue(item.starts_at),
    });
    globalThis.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (form.audience === "all" && !window.confirm(
      "Bu uzaktan seans duyurusu tüm portal kullanıcılarına açılacak. Devam edilsin mi?",
    )) return;
    setBusy(true);
    const payload = {
      ...form,
      position: form.audience === "client" ? supportedPosition(form.position) : "standing",
      repeat_count: Number(form.repeat_count),
      duration_minutes: Number(form.duration_minutes),
      starts_at: new Date(form.starts_at).toISOString(),
    };
    try {
      if (editingId) await api.put(`/remote-session-offers/${editingId}`, payload);
      else await api.post("/remote-session-offers", payload);
      toast.success(editingId ? "Duyuru güncellendi" : "Duyuru yayınlandı");
      reset();
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`“${item.title}” silinsin mi?`)) return;
    try {
      await api.delete(`/remote-session-offers/${item.id}`);
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const toggleAttendees = async (item) => {
    if (openAttendees === item.id) {
      setOpenAttendees(null);
      return;
    }
    setOpenAttendees(item.id);
    if (attendeeData[item.id]) return;
    try {
      const { data } = await api.get(`/remote-session-offers/${item.id}/attendees`);
      setAttendeeData((current) => ({ ...current, [item.id]: data }));
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const publishedCount = useMemo(
    () => items.filter((item) => item.published && item.session_status !== "completed").length,
    [items],
  );

  return (
    <div className="max-w-[1100px] p-3 pb-20 sm:p-5 lg:p-6">
      <header className="mb-4 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center bg-pleiades-blue text-white">
            <CalendarClock size={18} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold">Uzaktan Seans Duyuruları</h1>
            <p className="text-[12px] text-pleiades-slate">
              Seans akışını, katılımcıları ve hatırlatmaları tek alandan yönetin.
            </p>
          </div>
        </div>
        <span className="shrink-0 border border-pleiades-border bg-white px-3 py-2 text-[11px] text-pleiades-slate">
          {publishedCount} yaklaşan
        </span>
      </header>

      <form onSubmit={submit} className="mb-5 border border-pleiades-border bg-white p-4 sm:p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <strong className="text-[13px]">{editingId ? "Duyuruyu düzenle" : "Yeni seans duyurusu"}</strong>
            <p className="mt-1 text-[11px] text-pleiades-slate">Saatler İstanbul saatine göre gösterilir.</p>
          </div>
          {editingId && (
            <button type="button" aria-label="Düzenlemeyi kapat" onClick={reset} className="grid h-11 w-11 place-items-center">
              <X size={16} />
            </button>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Duyuru başlığı"><input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Kısa açıklama"><input className="input" required value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></Field>
          <Field label="Seans konusu"><input className="input" required value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} /></Field>
          <Field label="Seans niyeti"><input className="input" required value={form.intention} onChange={(e) => setForm({ ...form, intention: e.target.value })} /></Field>
          <Field label="Uygulama pozisyonu">
            <select
              className="input"
              value={form.audience === "client" ? supportedPosition(form.position) : "standing"}
              disabled={form.audience !== "client"}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
            >
              <option value="standing">Ayakta</option>
              <option value="sitting">Oturarak</option>
              <option value="lying">Uzanarak</option>
            </select>
            <span className="mt-1 block text-[10px] leading-relaxed text-pleiades-slate">
              Genel ve grup seanslarında pozisyon sabit olarak ayaktadır. Üç seçenek yalnız kişiye özel seansta kullanılabilir.
            </span>
          </Field>
          <Field label="Okunacak kod"><input className="input font-mono" required value={form.session_code} onChange={(e) => setForm({ ...form, session_code: e.target.value })} /></Field>
          <Field label="Tekrar sayısı"><input className="input" type="number" min="1" max="9999" required value={form.repeat_count} onChange={(e) => setForm({ ...form, repeat_count: e.target.value })} /></Field>
          <Field label="Başlangıç tarihi ve saati"><input className="input" type="datetime-local" required value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} /></Field>
          <Field label="Tahmini süre (dakika)"><input className="input" type="number" min="5" max="360" required value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} /></Field>
        </div>
        <div className="mt-3"><Field label="Hazırlık ve ayrıntılar"><textarea className="input min-h-24 py-3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field></div>
        <div className="mt-3"><AudienceSelector value={form} onChange={setForm} clients={clients} /></div>
        <label className="mt-3 flex min-h-11 items-center gap-2 text-[12px]"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Yayında</label>
        <button disabled={busy} className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 bg-pleiades-blue px-5 text-[13px] font-semibold text-white md:w-auto">
          <Plus size={15} />{busy ? "Kaydediliyor…" : editingId ? "Değişiklikleri kaydet" : "Duyuruyu yayınla"}
        </button>
      </form>

      <div className="space-y-3">
        {items.length === 0 && (
          <section className="border border-dashed border-pleiades-border bg-white p-8 text-center text-[12px] text-pleiades-slate">
            Henüz uzaktan seans duyurusu bulunmuyor.
          </section>
        )}
        {items.map((item) => {
          const expanded = openAttendees === item.id;
          const attendeeResult = attendeeData[item.id];
          return (
            <article key={item.id} className="border border-pleiades-border bg-white">
              <div className="p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center bg-pleiades-ivory text-pleiades-blue"><CalendarClock size={18} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[.1em] text-pleiades-blue">
                      <span>{statusText[item.session_status] || "Duyuru"}</span>
                      <span className="text-pleiades-border-strong">•</span>
                      <span>{item.published ? "Yayında" : "Taslak"}</span>
                    </div>
                    <h2 className="mt-1 text-[15px] font-semibold">{item.title}</h2>
                    <p className="mt-1 text-[12px] text-pleiades-slate">{formatSessionDate(item.starts_at)} · {item.duration_minutes} dakika</p>
                  </div>
                  <button aria-label="Düzenle" onClick={() => edit(item)} className="grid h-11 w-11 shrink-0 place-items-center"><Pencil size={15} /></button>
                  <button aria-label="Sil" onClick={() => remove(item)} className="grid h-11 w-11 shrink-0 place-items-center text-red-700"><Trash2 size={15} /></button>
                </div>
                <div className="mt-4 grid gap-px border border-pleiades-border bg-pleiades-border sm:grid-cols-3">
                  <Meta icon={BookOpenText} label="Konu" value={item.topic} />
                  <Meta icon={Hash} label="Kod ve tekrar" value={`${item.session_code} · ${item.repeat_count} kez`} mono />
                  <Meta icon={Users} label="Katılım" value={`${item.attendee_count || 0} kişi`} />
                </div>
                <div className="mt-3 text-[11px] text-pleiades-slate">{audienceLabel(item, clients)}</div>
                <button type="button" onClick={() => toggleAttendees(item)} className="mt-3 flex min-h-11 w-full items-center justify-between border border-pleiades-border px-3 text-[12px] font-semibold text-pleiades-ink">
                  <span className="flex items-center gap-2"><Users size={15} /> Katılımcıları gör</span>
                  {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>
              {expanded && (
                <div className="border-t border-pleiades-border bg-pleiades-ivory p-4">
                  {!attendeeResult && <p className="text-[11px] text-pleiades-slate">Katılımcılar yükleniyor…</p>}
                  {attendeeResult?.attendees?.length === 0 && <p className="text-[11px] text-pleiades-slate">Henüz katılım bildiren kullanıcı yok.</p>}
                  <div className="divide-y divide-pleiades-border">
                    {attendeeResult?.attendees?.map((attendee) => (
                      <div key={attendee.client_user_id} className="flex items-center justify-between gap-3 py-2.5 text-[12px]">
                        <strong className="font-medium">{attendee.name}</strong>
                        <span className="truncate text-pleiades-slate">{attendee.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="label">{label}</span>{children}</label>;
}

function Meta({ icon: Icon, label, value, mono = false }) {
  return (
    <div className="flex min-w-0 items-start gap-2 bg-white p-3">
      <Icon size={15} className="mt-0.5 shrink-0 text-pleiades-blue" aria-hidden="true" />
      <div className="min-w-0"><div className="text-[10px] text-pleiades-slate">{label}</div><div className={`mt-0.5 truncate text-[12px] font-medium ${mono ? "font-mono" : ""}`}>{value || "—"}</div></div>
    </div>
  );
}
